import { useEffect, useState } from 'react';
import { useJobProgressStream, type ProgressEvent } from './useJobProgressStream';

/**
 * Job timings panel.
 *
 * Shows three top-line metrics and a per-phase breakdown for a recon
 * job. Lives independently from the activity log (which the user can
 * collapse) so the summary stays visible after the job completes — a
 * specific user request after the original implementation hid timings
 * along with the event list.
 *
 * Subscribes to the same SSE stream as LiveActivityFeed because the
 * phase boundaries are derived from the event timestamps. When the
 * job is done, the stream is closed and the panel renders from the
 * final event list it has accumulated.
 *
 * If you mount BOTH this panel and a LiveActivityFeed on the same
 * page for the same job, they each open their own EventSource. That's
 * a small inefficiency but the stream is cheap (one HTTP connection,
 * one cookie, server-side it's just reading from Redis). Not worth a
 * shared-state refactor for the marginal saving.
 */
export function JobTimingsPanel({
  jobId,
  apiBaseUrl,
  createdAt,
  startedAt,
  updatedAt,
}: {
  jobId: string;
  apiBaseUrl: string;
  /** Job submission timestamp (created_at). Used for wall-clock time
   *  including queue wait. Optional — top-line metric falls back to '—'. */
  createdAt?: string | null;
  /** When the worker picked the job up (started_at). Null while still
   *  queued. */
  startedAt?: string | null;
  /** Most recent updated_at. For done jobs, this is the finish time. */
  updatedAt?: string | null;
}) {
  const { events, done } = useJobProgressStream(jobId, apiBaseUrl);

  // While the job is in flight, force a re-render every second so the
  // wall-clock and active-work counters tick visibly. Events don't
  // come in every second — sometimes the pipeline is silent for 30+
  // seconds during a long Claude call. Without this tick, the timing
  // numbers freeze even though real time is passing.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done]);

  // Top-line metrics
  const created = createdAt ? new Date(createdAt).getTime() : null;
  const started = startedAt ? new Date(startedAt).getTime() : null;
  const updated = updatedAt ? new Date(updatedAt).getTime() : null;

  // "End reference" picks the right closing time:
  //   - Done jobs: updated_at (the finish moment, frozen)
  //   - In-flight jobs: Date.now() (advances each tick)
  //   - Fallback: latest event timestamp
  const endRef = done
    ? updated
    : (events.length > 0
        ? Math.max(events[events.length - 1].ts, Date.now())
        : Date.now());

  const wallClockMs = (created != null && endRef != null) ? endRef - created : null;
  const activeMs = (started != null && endRef != null) ? endRef - started : null;

  // Pre-completion: first event in stream → cost.summary event
  const firstEvent = events[0];
  let preCompletionMs: number | null = null;
  for (const e of events) {
    if (e.phase === 'cost.summary' && firstEvent) {
      preCompletionMs = e.ts - firstEvent.ts;
      break;
    }
  }

  // Phase breakdown
  const phases = buildPhaseTimings(events);
  const maxPhaseMs = Math.max(0, ...phases.map(p => p.ms ?? 0));
  const hasAnyPhaseData = phases.some(p => p.ms != null);

  return (
    <div
      className="card mb-4 px-5 py-4"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-3">
        Timings
      </div>

      {/* Top-line row: three big numbers */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <TimingCard
          label="Wall-clock"
          value={formatDurationMs(wallClockMs)}
          hint="submission to finish (includes queue wait)"
        />
        <TimingCard
          label="Active work"
          value={formatDurationMs(activeMs)}
          hint="worker pickup to finish"
        />
        <TimingCard
          label="Pre-completion"
          value={formatDurationMs(preCompletionMs)}
          hint="extraction + recon + persist + cost calc"
        />
      </div>

      {/* Phase breakdown */}
      {hasAnyPhaseData && (
        <div className="rounded-md overflow-hidden border" style={{ borderColor: 'var(--glass-border)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'color-mix(in srgb, var(--text-200) 6%, transparent)' }}>
                <th className="px-3 py-1.5 text-left font-semibold text-ink-strong">Phase</th>
                <th className="px-3 py-1.5 text-right font-semibold text-ink-strong">Duration</th>
                <th className="px-3 py-1.5 text-left font-semibold text-ink-strong" style={{ width: '40%' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {phases.map(p => {
                const isSlowest = p.ms != null && p.ms === maxPhaseMs && maxPhaseMs > 0;
                const pct = (p.ms != null && maxPhaseMs > 0) ? (p.ms / maxPhaseMs) * 100 : 0;
                return (
                  <tr key={p.label} className="border-t"
                      style={{
                        borderColor: 'var(--glass-border)',
                        background: isSlowest ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : undefined,
                      }}>
                    <td className="px-3 py-1.5 text-ink-strong"
                        style={{ fontWeight: isSlowest ? 600 : 400 }}>
                      {p.label}
                      {isSlowest && <span className="ml-2 text-[10px] text-ink-400 uppercase tracking-wider">slowest</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-ink-strong"
                        style={{ fontWeight: isSlowest ? 600 : 400 }}>
                      {formatDurationMs(p.ms)}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="h-1.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--text-200) 10%, transparent)' }}>
                        <div className="h-full rounded-full"
                             style={{
                               width: `${pct}%`,
                               background: isSlowest ? 'var(--accent)' : 'color-mix(in srgb, var(--text-200) 40%, transparent)',
                               transition: 'width 200ms ease-out',
                             }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TimingCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div
      className="px-3 py-2 rounded-md"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--glass-border)',
      }}
      title={hint}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">{label}</div>
      <div className="text-lg font-mono tabular-nums font-semibold text-ink-strong">{value}</div>
    </div>
  );
}

/**
 * Phase-level timing extracted from the activity events. Pairing
 * heuristic — see comments in each step. Missing/unusual sequences
 * fall back to null rather than reporting wrong durations.
 */
interface PhaseTiming {
  label: string;
  ms: number | null;
}

function buildPhaseTimings(events: ProgressEvent[]): PhaseTiming[] {
  if (events.length === 0) return [];

  const first = (pred: (e: ProgressEvent) => boolean): ProgressEvent | null => {
    for (const e of events) if (pred(e)) return e;
    return null;
  };
  const last = (pred: (e: ProgressEvent) => boolean): ProgressEvent | null => {
    for (let i = events.length - 1; i >= 0; i--) {
      if (pred(events[i])) return events[i];
    }
    return null;
  };
  const span = (a: ProgressEvent | null, b: ProgressEvent | null): number | null => {
    if (!a || !b) return null;
    const d = b.ts - a.ts;
    return d >= 0 ? d : null;
  };

  // 1. Portfolio parse
  const pfStart = first(e => e.phase === 'portfolio.parse' && e.level === 'info');
  const pfEnd = first(e => e.phase === 'portfolio.parse' && e.level === 'success');
  const portfolioMs = span(pfStart, pfEnd);

  // 2. DP extract/parse — first dp.* info to last dp.* success.
  //    Covers single-DP-file jobs AND multi-DP jobs.
  const isDpEvent = (e: ProgressEvent) => e.phase.startsWith('dp.');
  const dpStart = first(e => isDpEvent(e) && e.level === 'info');
  const dpEnd = last(e => isDpEvent(e) && e.level === 'success');
  const dpMs = span(dpStart, dpEnd);

  // 3. Reconciliation — net-balance mode usually emits a single
  //    txn.match success. For single-event phases use the delta from
  //    the immediately-preceding event to estimate the engine time.
  const reconStart = first(e => e.phase === 'txn.match');
  const reconEnd = last(e => e.phase === 'txn.match');
  let reconMs: number | null = null;
  if (reconStart && reconEnd && reconStart !== reconEnd) {
    reconMs = span(reconStart, reconEnd);
  } else if (reconStart) {
    const idx = events.indexOf(reconStart);
    if (idx > 0) reconMs = reconStart.ts - events[idx - 1].ts;
  }

  // 4. Persist (DB write) — persist info → persist.done (the precise
  //    DB-write span). Falls back to cost.summary for jobs that ran
  //    before persist.done was emitted.
  const persistStart = first(e => e.phase === 'persist' && e.level === 'info');
  const persistDone = first(e => e.phase === 'persist.done');
  const costSummary = first(e => e.phase === 'cost.summary');
  const persistMs = span(persistStart, persistDone ?? costSummary);

  // 5. Cost summary → job.complete (essentially zero on success)
  const jobComplete = first(e => e.phase === 'job.complete' || e.phase === 'job.failed');
  const costMs = span(costSummary, jobComplete);

  return [
    { label: 'Portfolio parse',  ms: portfolioMs },
    { label: 'DP extract/parse', ms: dpMs },
    { label: 'Reconciliation',   ms: reconMs },
    { label: 'Saving results to database', ms: persistMs },
    { label: 'Cost summary',     ms: costMs },
  ];
}

/**
 * Format a millisecond duration as a glanceable string with ms
 * precision always shown.
 *
 *   < 1000 ms : '847ms'
 *   < 60 s    : '4.612s'
 *   ≥ 60 s    : '1m 5.230s'
 */
function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(3)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = (ms % 60_000) / 1000;
  return `${mins}m ${secs.toFixed(3)}s`;
}
